import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  ReceiptLong as ReceiptLongIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import Can from '../../../components/Can';
import { ROUTES } from '../../../config/routes';
import contratoVentaService, {
  ContratoKpis,
  ContratoListItem,
  ContratoPagedResult,
  ContratoTipo,
} from '../../../services/contratoVentaService';
import { formatCurrency } from '../../../utils/formatters';
import ContratosKpisBar from './ContratosKpisBar';

const estadoColor = (estado: string): 'default' | 'success' | 'warning' | 'error' | 'info' => {
  switch (estado) {
    case 'Vigente':
      return 'success';
    case 'Borrador':
      return 'default';
    case 'Suspendido':
      return 'warning';
    case 'Finalizado':
      return 'info';
    case 'Anulado':
      return 'error';
    default:
      return 'default';
  }
};

const Contratos: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [data, setData] = useState<ContratoPagedResult>({
    items: [],
    totalCount: 0,
    pageNumber: 1,
    pageSize: 10,
    totalPages: 0,
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [estado, setEstado] = useState('');
  const [tipoContratoId, setTipoContratoId] = useState('');
  const [tipos, setTipos] = useState<ContratoTipo[]>([]);
  const [loading, setLoading] = useState(false);
  const [kpis, setKpis] = useState<ContratoKpis | null>(null);
  const [kpisLoading, setKpisLoading] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const result = await contratoVentaService.getAll({
        pageNumber: page + 1,
        pageSize: rowsPerPage,
        searchTerm: searchTerm || undefined,
        estado: estado || undefined,
        tipoContratoId: tipoContratoId ? Number(tipoContratoId) : undefined,
      });
      setData(result);
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Error al cargar contratos', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchKpis = async () => {
    try {
      setKpisLoading(true);
      setKpis(await contratoVentaService.getKpis());
    } catch {
      setKpis(null);
    } finally {
      setKpisLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => fetchData(), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, searchTerm, estado, tipoContratoId]);

  useEffect(() => {
    contratoVentaService.getTipos().then(setTipos).catch(() => setTipos([]));
  }, []);

  useEffect(() => {
    fetchKpis();
  }, []);

  const renderRow = (c: ContratoListItem) => (
    <TableRow key={c.id} hover>
      <TableCell>{c.numero}</TableCell>
      <TableCell>{c.codigoContrato || '—'}</TableCell>
      <TableCell>{c.tipoContratoNombre || '—'}</TableCell>
      <TableCell>
        {c.nroCliente} — {c.nombreCliente || '—'}
      </TableCell>
      <TableCell>{c.fechaInicio?.toString().slice(0, 10)}</TableCell>
      <TableCell>{c.fechaFin?.toString().slice(0, 10)}</TableCell>
      <TableCell>{c.periodicidad}</TableCell>
      <TableCell>{c.monedaDescripcion || c.codigoMoneda}</TableCell>
      <TableCell align="right">{formatCurrency(c.montoMensualEstimado)}</TableCell>
      <TableCell>
        <Chip size="small" label={c.estado} color={estadoColor(c.estado)} />
      </TableCell>
      <TableCell align="center">
        {(c.periodosPendientes || 0) + (c.certificacionesPendientes || 0)}
      </TableCell>
      <TableCell align="right">
        <Tooltip title="Editar">
          <IconButton size="small" onClick={() => navigate(ROUTES.ventas.contratos.editar(c.id))}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', sm: 'center' }}
            spacing={2}
            sx={{ mb: 2 }}
          >
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Contratos de venta
            </Typography>
            <Stack direction="row" spacing={1}>
              <Can permiso="ventas.contratos.facturar">
                <Button
                  variant="outlined"
                  startIcon={<ReceiptLongIcon />}
                  onClick={() => navigate(ROUTES.ventas.contratos.facturacion)}
                >
                  Facturación masiva
                </Button>
              </Can>
              <Can permiso="ventas.contratos.nuevo">
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => navigate(ROUTES.ventas.contratos.nuevo)}
                >
                  Nuevo
                </Button>
              </Can>
            </Stack>
          </Stack>

          <ContratosKpisBar kpis={kpis} loading={kpisLoading} />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Buscar por número, código, cliente u OC..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              select
              size="small"
              label="Estado"
              value={estado}
              onChange={(e) => {
                setEstado(e.target.value);
                setPage(0);
              }}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="Borrador">Borrador</MenuItem>
              <MenuItem value="Vigente">Vigente</MenuItem>
              <MenuItem value="Suspendido">Suspendido</MenuItem>
              <MenuItem value="Finalizado">Finalizado</MenuItem>
              <MenuItem value="Anulado">Anulado</MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              label="Tipo"
              value={tipoContratoId}
              onChange={(e) => {
                setTipoContratoId(e.target.value);
                setPage(0);
              }}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="">Todos</MenuItem>
              {tipos.filter((t) => t.activo).map((t) => (
                <MenuItem key={t.id} value={String(t.id)}>
                  {t.nombre}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Nº</TableCell>
                  <TableCell>Código</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Cliente</TableCell>
                  <TableCell>Inicio</TableCell>
                  <TableCell>Fin</TableCell>
                  <TableCell>Periodicidad</TableCell>
                  <TableCell>Moneda</TableCell>
                  <TableCell align="right">Importe período</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell align="center">Pendientes</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={12} align="center">
                      Cargando...
                    </TableCell>
                  </TableRow>
                ) : data.items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={12} align="center">
                      No hay contratos
                    </TableCell>
                  </TableRow>
                ) : (
                  data.items.map(renderRow)
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={data.totalCount}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            labelRowsPerPage="Filas"
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default Contratos;
